import {
  Column,
  DataTableSkeleton,
  FlexGrid as Grid,
  InlineNotification,
  Row,
  Tile,
} from '@carbon/react';
import { useAtomValue } from 'jotai';
import { useUnitTableAtom } from './atoms';
import { Section } from 'components/section';
import { EmptyState } from 'components/empty-state';
import { WellsResponse } from 'types/api-responses';
import type { WellCompletionRow } from 'types/well-completions';
import style from './well-page-content.module.scss';
import UnitTable from './unit-details-table';
import DevAreaSvg from 'images/srp-development-areas.svg';
import {
  pickSections,
  useWellPageSections,
  type WellPageSection,
  type WellPageSectionId,
} from './well-page-sections';

const UnitsTableSection = ({ href }: { href: string }) => {
  const unitsTableAtom = useUnitTableAtom(href);
  const unitsTableData = useAtomValue(unitsTableAtom);

  if (unitsTableData.loading) {
    return (
      <DataTableSkeleton
        showHeader={false}
        showToolbar={false}
        columnCount={7}
        rowCount={5}
      />
    );
  }

  if (!unitsTableData.data) {
    return null;
  }

  if ('error' in unitsTableData.data) {
    return (
      <InlineNotification
        kind="error"
        title="Notification"
        lowContrast
        subtitle={'Error loading units table, Please try again'}
      />
    );
  }

  if (!unitsTableData.data.length) {
    return (
      <EmptyState
        icon={DevAreaSvg}
        headerText="No unit details available for this well"
      />
    );
  }

  return <UnitTable data={unitsTableData.data} />;
};

// The sections the grid places itself: Well Details, Well Status and Unit Details stack
// in the left column with the Map beside them. Everything after gets a full-width row.
// Well Status sits right under Well Details because that is where its fields — the SRP
// status and the lifecycle dates — lived before mock v13 split them into their own card.
const PAIRED_WITH_MAP: WellPageSectionId[] = [
  'well-details',
  'well-status',
  'unit-details',
];

// The grid's own section manifest: the eleven sections it shows (Related Well Files
// left with the Well page, its files now under Related Items), in their long-standing
// order. Deliberately NOT the card layout's set — the card layout
// follows mock v13 and adds EUR, and the only other caller of this grid is the packages
// evaluation-result well panel, which should not change. A new shared section has to be
// listed here to appear in the grid.
const GRID_ORDER: WellPageSectionId[] = [
  'well-details',
  'well-status',
  'unit-details',
  'map',
  'permits',
  'wellbores',
  'wellbore-completions',
  'stimulations',
  'subsurface-explorer',
  'well-analysis',
  'related-items',
];

/**
 * One section in the grid. A section whose body carries its own heading — the three
 * accordions, Related Items — is rendered as-is; the rest get the
 * shared `Section` header.
 * @param {{ section: WellPageSection }} props The section to frame.
 * @return {JSX.Element} The framed section.
 */
const GridSection = ({ section }: { section: WellPageSection }) => {
  if (section.ownsHeading) return <>{section.body}</>;

  return <Section header={section.label}>{section.body}</Section>;
};

const WellPageContent = ({
  well,
  uwi,
  hideMap,
  versionId,
  completions,
  completionsError,
}: {
  well?: WellsResponse;
  uwi: string;
  hideMap?: boolean;
  versionId?: string;
  /** The well's completions, read by the caller with `useWellCompletions` from `lib/wells`. */
  completions?: WellCompletionRow[];
  /** The failed completions read, or null. */
  completionsError?: Error | null;
}) => {
  const sections = pickSections(
    useWellPageSections({
      well,
      uwi,
      versionId,
      completions,
      completionsError,
    }),
    GRID_ORDER.filter((id) => {
      if (id === 'map') return !hideMap;

      return true;
    })
  );

  const find = (id: WellPageSectionId) =>
    sections.find((section) => section.id === id);
  const mapSection = find('map');
  // The rest, in page order, each in its own row.
  const rows = sections.filter(
    (section) => section.id !== 'map' && !PAIRED_WITH_MAP.includes(section.id)
  );

  const gridProps = hideMap ? {} : { md: 12, lg: 10 };

  return (
    <Grid className="bx--no-gutter" fullWidth condensed>
      <Row className={style.mapWellRow}>
        <Column {...gridProps} className={style.colDetails}>
          <div className={style.wrapper}>
            {PAIRED_WITH_MAP.map((id) => {
              const section = find(id);

              return section ? (
                <Tile key={section.id} className={style.rowBlock}>
                  <GridSection section={section} />
                </Tile>
              ) : null;
            })}
          </div>
        </Column>
        {mapSection && (
          <Column md={12} lg={6} className={style.colMap}>
            <Tile className={style.mapTile}>
              <GridSection section={mapSection} />
            </Tile>
          </Column>
        )}
      </Row>

      {rows.map((section) => (
        <Row key={section.id}>
          <Column>
            <Tile className={style.rowBlock}>
              <GridSection section={section} />
            </Tile>
          </Column>
        </Row>
      ))}
    </Grid>
  );
};

export { WellPageContent, UnitsTableSection };
